import React, { useState } from "react";
import { usePriceCurrency } from '../../../hooks/useCurrency';
import { formatPlanPrice } from './planCatalogue';
import { useTranslation } from "react-i18next";
import ModalShell from "../ModalShell";
import Button from "../TailwindComponents/Button";
import CheckBox from "../TailwindComponents/CheckBox";
import FieldShell, { fieldSurface } from "../TailwindComponents/FieldShell";
import { PiUsersLight, PiSparkleLight, PiCrownLight } from "react-icons/pi";
import { LicensePlan } from "../../../store/actions/PlanActions";

export interface FullPlanSuggestion {
  fullPlan: LicensePlan;
  fullPlanPrice: number;
  currentTotalWithAddon: number;
}

interface AddAddonModalProps {
  open: boolean;
  onClose: () => void;
  addon: LicensePlan | null;
  billingPeriod: "monthly" | "annual";
  teamSize: number;
  onConfirm: (addon: LicensePlan, seatCount: number, applyTeamDiscount: boolean) => Promise<void>;
  fullPlanSuggestion?: FullPlanSuggestion | null;
  onUpgradeToFull?: (plan: LicensePlan) => void;
}

const AddAddonModal: React.FC<AddAddonModalProps> = ({
  open,
  onClose,
  addon,
  billingPeriod,
  teamSize,
  onConfirm,
  fullPlanSuggestion,
  onUpgradeToFull,
}) => {
  const { t } = useTranslation();
  const priceCurrency = usePriceCurrency();
  // Licence prices are in what the firm is billed in, never a hard-coded symbol.
  const money = (n: number, d = 0) => formatPlanPrice(n, priceCurrency, d);
  const [seatInput, setSeatInput] = useState("1");
  const [wholeTeam, setWholeTeam] = useState(false);
  const [loading, setLoading] = useState(false);

  React.useEffect(() => {
    if (open) {
      setSeatInput("1");
      setWholeTeam(false);
    }
  }, [open]);

  if (!addon) return null;

  const price = billingPeriod === "monthly" ? addon.price_monthly : addon.price_annual;
  const teamDiscount = addon.team_discount_percent || 0;
  const teamPrice = teamDiscount > 0 ? price * (1 - teamDiscount / 100) : price;

  const seatCount = wholeTeam ? teamSize : Math.max(1, parseInt(seatInput, 10) || 1);
  const effectivePrice = wholeTeam && teamDiscount > 0 ? teamPrice : price;
  const totalMonthly = effectivePrice * seatCount;

  const handleConfirm = async () => {
    setLoading(true);
    try {
      await onConfirm(addon, seatCount, wholeTeam && teamDiscount > 0);
      onClose();
    } finally {
      setLoading(false);
    }
  };

  return (
    <ModalShell
      isOpen={open}
      onClose={onClose}
      title={<>{t("Add")} {addon.display_name}</>}
      icon={<PiSparkleLight />}
      size="sm"
      footer={
        <>
          <Button variant="neutral" size="small" onClick={onClose}>
            {t("Cancel")}
          </Button>
          <Button
            variant="primary"
            size="small"
            onClick={handleConfirm}
            isLoading={loading}
          >
            {t("Add add-on")}
          </Button>
        </>
      }
    >
              {/* Body */}
              <div className="space-y-4">
                {/* Team toggle */}
                {addon.is_per_seat && teamDiscount > 0 && teamSize > 1 && (
                  <div className="flex items-center gap-3 p-3 rounded-lg border border-hairline hover:bg-surface-sunken/70 transition-colors">
                    <CheckBox
                      label={t("Buy for whole team")}
                      hideLabel
                      translate={false}
                      checked={wholeTeam}
                      onChange={(e) => setWholeTeam(e.target.checked)}
                    />
                    <div className="flex-1">
                      <p className="text-sm text-fg-strong">
                        {t("Buy for whole team")} ({teamSize} {t("members")})
                      </p>
                      <p className="text-xs text-success-600 dark:text-success-400">
                        {t("Save")} {teamDiscount}% - {money(teamPrice, 0)}/{t("seat")}/{t("mo")}
                      </p>
                    </div>
                  </div>
                )}

                {/* Seat count input (only when not whole team) */}
                {addon.is_per_seat && !wholeTeam && (
                  <FieldShell label={t("Number of seats")} translate={false} id="addon-seat-count">
                    <input
                      id="addon-seat-count"
                      type="number"
                      min={1}
                      value={seatInput}
                      onChange={(e) => setSeatInput(e.target.value)}
                      className={`${fieldSurface()} px-3 py-2`}
                    />
                  </FieldShell>
                )}

                {/* Cost summary */}
                <div className="rounded-lg border border-hairline bg-surface-sunken/70 p-3 space-y-1.5 text-xs">
                  <div className="flex justify-between text-fg-muted">
                    <span>{t("Price per seat")}</span>
                    <span>
                      {money(effectivePrice, 2)}/{t("mo")}
                      {wholeTeam && teamDiscount > 0 && (
                        <span className="line-through text-fg-muted ms-1">{money(price, 0)}</span>
                      )}
                    </span>
                  </div>
                  <div className="flex justify-between text-fg-muted">
                    <span>{t("Seats")}</span>
                    <span>{seatCount}</span>
                  </div>
                  <hr className="border-hairline" />
                  <div className="flex justify-between text-fg-strong font-medium">
                    <span>{t("Total")}</span>
                    <span>
                      {money(totalMonthly, 2)}/{t("mo")}
                      {billingPeriod === "annual" && (
                        <span className="text-fg-muted font-normal ms-1">
                          ({t("billed annually")})
                        </span>
                      )}
                    </span>
                  </div>
                </div>

                {/* Full-plan value nudge */}
                {fullPlanSuggestion && fullPlanSuggestion.fullPlanPrice <= fullPlanSuggestion.currentTotalWithAddon && (
                  <div
                    className="flex items-start gap-2 rounded-lg border border-primary-200 dark:border-primary-800 bg-primary-50 dark:bg-primary-900/20 p-3 cursor-pointer hover:bg-primary-100 dark:hover:bg-primary-900/30 transition-colors"
                    onClick={() => {
                      onClose();
                      onUpgradeToFull?.(fullPlanSuggestion.fullPlan);
                    }}
                  >
                    <PiCrownLight className="h-4 w-4 text-primary-600 dark:text-primary-400 flex-shrink-0 mt-0.5" />
                    <p className="text-xs text-fg-body">
                      {t("Consider upgrading to {{plan}} at {{price}}/mo — includes this and all other add-ons.", {
                        plan: fullPlanSuggestion.fullPlan.display_name,
                        price: money(fullPlanSuggestion.fullPlanPrice),
                      })}
                    </p>
                  </div>
                )}

                <p className="text-xs text-fg-muted">
                  {t("You will be charged immediately for the prorated amount.")}
                </p>
              </div>

    </ModalShell>
  );
};

export default AddAddonModal;
